import React, { useState } from "react"
import { Command } from "@kit/design/command"
import { LoadingAnimation } from "@kit/design/loading-animation"
import Icon from "@kit/design/icon"
import { navigate } from "core/navigation"
import { isStaticFeatureEnabled } from "hooks/use-feature"
import { SUBPAGES } from "./pages"
import { useCommandPaletteContext } from "./use-command-palette-context"
import { useCommandProviders, getShortcuts } from "./providers"
import { useCommandPaletteStore } from "./store"
import { useCommandPaletteHotkeys } from "./use-command-palette-hotkeys"

const groupBy = commands => {
  const groups = new Map()
  for (const command of commands) {
    const { group } = command
    if (!groups.has(group)) groups.set(group, [])
    groups.get(group).push(command)
  }
  return [...groups.entries()]
}

export function CommandPalette() {
  // Gate the whole palette — including its global hotkeys — behind the flag.
  // A hook-free wrapper so the inner component's hooks stay unconditional.
  if (!isStaticFeatureEnabled("command_palette")) return null
  return <CommandPaletteDialog />
}

function CommandPaletteDialog() {
  const open = useCommandPaletteStore(state => state.open)
  const setOpen = useCommandPaletteStore(state => state.setOpen)
  const [query, setQuery] = useState("")
  // Stack of subpage ids; the last entry is the active subpage (root = none).
  const [pages, setPages] = useState([])
  const activePageId = pages.at(-1) ?? null

  const context = useCommandPaletteContext()
  const { commands, isLoading } = useCommandProviders({
    query,
    activePageId,
    context
  })

  const reset = () => {
    setQuery("")
    setPages([])
  }

  const handleOpenChange = (next, eventDetails) => {
    // On a subpage, Escape steps back to the parent instead of closing the
    // palette. `open` is controlled, so simply not flipping it keeps the dialog
    // open while we pop. (Base UI's close reason — Escape vs. backdrop.)
    if (!next && eventDetails?.reason === "escape-key" && pages.length > 0) {
      popPage()
      return
    }
    setOpen(next)
    if (!next) reset() // start fresh on the next open
  }

  // Opens the palette too, so a global chord (fired while closed) lands
  // straight on the subpage; a no-op open() when already open.
  const pushPage = pageId => {
    setPages(current => [...current, pageId])
    setQuery("")
    setOpen(true)
  }

  const popPage = () => {
    setPages(current => current.slice(0, -1))
    setQuery("")
  }

  const api = {
    close: () => handleOpenChange(false),
    navigate: path => {
      handleOpenChange(false)
      navigate(path)
    },
    pushPage,
    popPage
  }

  const shortcuts = getShortcuts(context)
  const toggleOpen = () => (open ? handleOpenChange(false) : setOpen(true))
  useCommandPaletteHotkeys({ open, toggleOpen, shortcuts, api })

  // On a subpage, Backspace with an empty input steps back to the parent.
  const handleInputKeyDown = event => {
    if (event.key === "Backspace" && query === "" && pages.length > 0) {
      event.preventDefault()
      popPage()
    }
  }

  const subpage = activePageId ? SUBPAGES[activePageId] : null
  // The root always uses cmdk's client-side filtering; a subpage decides for
  // itself (see SUBPAGES) — e.g. server-filtered results opt out.
  const shouldFilter = subpage ? subpage.shouldFilter : true

  // Below a subpage's minQueryLength there are no results *yet* — nudge the user
  // to keep typing rather than claim their search came up empty.
  const emptyText = () => {
    if (!subpage) return "No results found."
    const min = subpage.minQueryLength
    if (min && query.trim().length < min) {
      return `Type at least ${min} characters to search.`
    }
    return subpage.emptyText
  }

  const renderItem = command => (
    <Command.Item
      key={command.id}
      // `value` is what cmdk matches the query against when filtering is on, so
      // it must carry the searchable text (label + keywords). When filtering is
      // off, cmdk only uses `value` as the item's identity for keyboard
      // selection, so a unique id keeps items that share a label distinct.
      value={
        shouldFilter
          ? [command.label, ...(command.keywords || [])].join(" ")
          : command.id
      }
      onSelect={() => command.onSelect(api)}
    >
      {command.icon && <Icon name={command.icon} />}
      <span className="min-w-0 truncate">{command.label}</span>
      {command.shortcut && (
        <Command.Shortcut keys={command.shortcut.split(" ")} />
      )}
      <Command.Hint>
        Press <Command.Kbd>↵</Command.Kbd>
      </Command.Hint>
    </Command.Item>
  )

  return (
    <Command.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      shouldFilter={shouldFilter}
    >
      <Command.Input
        placeholder={subpage ? subpage.placeholder : "Type to search..."}
        value={query}
        onValueChange={setQuery}
        onKeyDown={handleInputKeyDown}
        onBack={activePageId ? popPage : undefined}
      />
      <Command.List>
        {isLoading && commands.length === 0 ? (
          <div className="flex justify-center py-4">
            <LoadingAnimation />
          </div>
        ) : (
          <Command.Empty>{emptyText()}</Command.Empty>
        )}
        {groupBy(commands).map(([heading, items]) => (
          <Command.Group key={heading} heading={heading}>
            {items.map(renderItem)}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  )
}
